import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { StreamChat } from 'stream-chat';
import { AuthContext } from '../App';
import { Phone, PhoneOff, Video, X, MessageSquare, Radio } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Widget global de llamadas entrantes desde Stream Chat (DM o canales).
// Se monta en Layout una sola vez y permanece activo en TODAS las páginas
// mientras el usuario esté logueado, mostrando el popup con timbre cuando
// alguien inicia una videollamada en cualquier canal/DM en el que sea miembro.

// Optimización para mobile: ajusta la URL del room Daily para que en pantallas
// chicas se priorice la vista de "active speaker" (quien habla ocupa la pantalla)
// y se oculten elementos de UI que sobrecargan en celular.
const optimizeDailyUrlForMobile = (url) => {
  if (!url || typeof window === 'undefined') return url;
  const isMobile = window.innerWidth < 768;
  if (!isMobile) return url;
  try {
    const u = new URL(url);
    // Daily Prebuilt detecta automáticamente mobile pero forzamos los parámetros
    // que controlan layout y UI compacta — son ignorados si no aplican.
    if (!u.searchParams.has('showLocalVideo')) u.searchParams.set('showLocalVideo', 'true');
    if (!u.searchParams.has('showParticipantsBar')) u.searchParams.set('showParticipantsBar', 'false');
    if (!u.searchParams.has('showFullscreenButton')) u.searchParams.set('showFullscreenButton', 'false');
    // `customLayout` y `subscribeToTracksAutomatically` controlables vía SDK,
    // pero el flag de UI activeSpeaker se intenta vía URL como hint:
    if (!u.searchParams.has('layout')) u.searchParams.set('layout', 'active-speaker');
    return u.toString();
  } catch (_e) {
    return url;
  }
};

const buildJoinUrl = async (url, userName) => {
  if (!url) return url;
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    const resp = await fetch(`${origin}/api/chat/video-join-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ room_url: url, user_name: userName })
    });
    if (!resp.ok) return optimizeDailyUrlForMobile(url);
    const data = await resp.json();
    return optimizeDailyUrlForMobile(data.url || url);
  } catch (_e) {
    return optimizeDailyUrlForMobile(url);
  }
};

const ChatIncomingCall = () => {
  const { API, user } = useContext(AuthContext);
  const [client, setClient] = useState(null);
  const [llamada, setLlamada] = useState(null); // { url, caller, channelType, channelId, channelName, at }
  const [activeCall, setActiveCall] = useState(null); // { url, title, channelType, channelId }
  const [isMinimized, setIsMinimized] = useState(false);
  // Resetear modo minimizado al cerrar la llamada
  useEffect(() => {
    if (!activeCall) setIsMinimized(false);
  }, [activeCall]);
  const audioCtxRef = useRef(null);
  const ringTimerRef = useRef(null);
  const recentCallIdsRef = useRef(new Set()); // para no repetir popup en la misma llamada

  // 1) Conexión global a Stream Chat (singleton) — evita duplicar la conexión que ya hace StreamChatComponent
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let chatClient = null;

    const connect = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/stream-chat/token`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { token: streamToken, user_id, user_name, user_image, api_key } = res.data || {};
        if (!streamToken || !user_id || !api_key || cancelled) return;
        chatClient = StreamChat.getInstance(api_key);
        // Si ya está conectado al mismo usuario, no reconectar
        if (chatClient.userID !== user_id) {
          await chatClient.connectUser(
            { id: user_id, name: user_name, image: user_image || undefined },
            streamToken
          );
        }
        if (!cancelled) setClient(chatClient);
      } catch (e) {
        // Silencioso: si falla, simplemente no habrá popups (chat directo igual sigue funcionando)
        console.warn('ChatIncomingCall: no se pudo conectar a Stream Chat', e?.message || e);
      }
    };

    connect();
    return () => { cancelled = true; };
  }, [API, user]);

  // 2) Listener global para mensajes con video_call
  useEffect(() => {
    if (!client) return;

    const handleVideoCallEvent = (event) => {
      const msg = event?.message;
      if (!msg) return;
      // Los campos custom pueden llegar top-level (JS SDK) o dentro de message.custom (Python SDK / backend)
      const custom = msg.custom || {};
      const vc = msg.video_call ?? custom.video_call;
      const vcUrl = msg.video_call_url ?? custom.video_call_url;
      const vcCaller = msg.video_call_caller ?? custom.video_call_caller;
      const vcStartedAt = msg.video_call_started_at ?? custom.video_call_started_at;
      const isPtt = !!(msg.is_ptt ?? custom.is_ptt ?? msg.ptt_call ?? custom.ptt_call);
      if (!vc || !vcUrl) return;
      // No popup si la llamada la hizo este mismo usuario
      if (msg.user?.id === client.userID) return;
      // Si ya hay una llamada activa o un popup abierto, no apilar
      if (activeCall) return;
      // Evitar mostrar llamadas viejas (más de 60 seg)
      const startedAt = vcStartedAt || (msg.created_at ? new Date(msg.created_at).getTime() : 0);
      if (startedAt && Date.now() - startedAt > 60000) return;
      // Idempotencia: no volver a mostrar la misma llamada
      const callKey = `${msg.id || ''}-${vcUrl}`;
      if (recentCallIdsRef.current.has(callKey)) return;
      recentCallIdsRef.current.add(callKey);
      setTimeout(() => recentCallIdsRef.current.delete(callKey), 5 * 60 * 1000);

      setLlamada({
        url: vcUrl,
        caller: vcCaller || msg.user?.name || 'Alguien',
        channelType: event.channel_type,
        channelId: event.channel_id,
        channelName: event.channel?.name || event.cid || '',
        at: startedAt,
        isPtt,
      });
    };

    client.on('message.new', handleVideoCallEvent);
    client.on('notification.message_new', handleVideoCallEvent);

    return () => {
      client.off('message.new', handleVideoCallEvent);
      client.off('notification.message_new', handleVideoCallEvent);
    };
  }, [client, activeCall]);

  // 2.5) Listener global para abrir llamada PTT (iniciador) o video call desde cualquier parte de la app
  useEffect(() => {
    const openHandler = (ev) => {
      const d = ev?.detail || {};
      if (!d.url) return;
      setActiveCall({
        url: d.url,
        title: d.title || (d.isPtt ? 'Radio en vivo' : 'Videollamada'),
        channelType: d.channelType || 'messaging',
        channelId: d.channelId || null,
        isPtt: !!d.isPtt,
      });
    };
    window.addEventListener('iba:open-ptt-call', openHandler);
    return () => window.removeEventListener('iba:open-ptt-call', openHandler);
  }, []);

  // 3) Timbre + auto-cierre del popup
  useEffect(() => {
    if (!llamada) {
      if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
      try { audioCtxRef.current?.close?.(); } catch (_e) {}
      audioCtxRef.current = null;
      return;
    }
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        audioCtxRef.current = ctx;
        const playBeep = () => {
          if (ctx.state === 'suspended') ctx.resume().catch(() => {});
          [0, 400].forEach((delay) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.value = 880;
            const start = ctx.currentTime + delay / 1000;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(0.3, start + 0.02);
            gain.gain.linearRampToValueAtTime(0, start + 0.25);
            osc.start(start);
            osc.stop(start + 0.3);
          });
        };
        playBeep();
        ringTimerRef.current = setInterval(playBeep, 1600);
      }
    } catch (_e) {}

    // Vibración móvil opcional
    try { navigator.vibrate?.([300, 200, 300, 200, 300]); } catch (_e) {}

    // Cerrar automáticamente si nadie atiende en 45s
    const tid = setTimeout(() => setLlamada(null), 45000);
    return () => {
      clearTimeout(tid);
      if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
      try { audioCtxRef.current?.close?.(); } catch (_e) {}
      audioCtxRef.current = null;
    };
  }, [llamada]);

  const aceptar = async () => {
    if (!llamada) return;
    const userName = client?.user?.name || 'Invitado';
    const finalUrl = await buildJoinUrl(llamada.url, userName);
    setActiveCall({
      url: finalUrl,
      title: llamada.isPtt ? `Radio · ${llamada.caller}` : `Videollamada · ${llamada.caller}`,
      channelType: llamada.channelType,
      channelId: llamada.channelId,
      isPtt: !!llamada.isPtt,
    });
    setLlamada(null);
    toast.success(`Conectando con ${llamada.caller}...`);
  };

  const rechazar = () => {
    setLlamada(null);
    toast.info('Llamada rechazada');
  };

  const finalizar = () => {
    setActiveCall(null);
  };

  if (!llamada && !activeCall) return null;

  return (
    <>
      {llamada && createPortal(
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="chat-incoming-call"
        >
          <div className={`${llamada.isPtt ? 'bg-gradient-to-br from-orange-600 via-amber-500 to-yellow-500' : 'bg-gradient-to-br from-emerald-600 via-green-500 to-teal-500'} rounded-3xl shadow-2xl p-8 max-w-md w-full text-white animate-pulse`}>
            <div className="text-center mb-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 ring-4 ring-white/40 ring-offset-4 ring-offset-transparent animate-bounce">
                {llamada.isPtt ? <Radio className="w-12 h-12 text-white" /> : <Video className="w-12 h-12 text-white" />}
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-bold mb-2 tracking-wide">
                {llamada.isPtt ? <Radio className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
                {llamada.isPtt ? 'RADIO ENTRANTE' : 'VIDEOLLAMADA ENTRANTE'}
              </div>
              <h2 className="text-2xl font-bold mb-1" data-testid="chat-caller-name">
                {llamada.caller}
              </h2>
              <p className="text-white/80 text-xs mt-2">
                {llamada.isPtt ? 'te está llamando por Radio (solo audio)...' : 'te está llamando por chat...'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={rechazar}
                className="py-4 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-2xl font-semibold shadow transition-all active:scale-95 flex items-center justify-center gap-2"
                data-testid="chat-reject-btn"
              >
                <PhoneOff className="w-5 h-5" />
                Rechazar
              </button>
              <button
                onClick={aceptar}
                className="py-4 bg-white text-green-700 rounded-2xl font-bold shadow-lg transition-all active:scale-95 hover:bg-green-50 flex items-center justify-center gap-2"
                data-testid="chat-accept-btn"
              >
                <Phone className="w-5 h-5" />
                Atender
              </button>
            </div>

            <p className="text-center text-white/60 text-xs mt-5">
              La llamada expira en ~45 segundos
            </p>
          </div>
        </div>,
        document.body
      )}

      {activeCall && createPortal(
        <div
          className={
            isMinimized
              ? "fixed bottom-4 right-4 z-[9999] bg-black rounded-2xl shadow-2xl border-2 border-emerald-500 flex flex-col overflow-hidden w-[420px] h-[280px]"
              : "fixed inset-0 z-[9999] bg-black flex flex-col"
          }
          style={isMinimized ? { resize: 'both', overflow: 'hidden' } : { height: '100dvh', maxHeight: '100dvh' }}
          data-testid="chat-active-call-panel"
        >
          <div className={`flex items-center justify-between bg-gray-900 shrink-0 ${isMinimized ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
            <div className="flex items-center gap-2 text-white min-w-0">
              <Video className={isMinimized ? 'w-3.5 h-3.5 shrink-0' : 'w-4 h-4 shrink-0'} />
              <span className={`font-medium truncate ${isMinimized ? 'text-xs' : 'text-sm'}`}>
                {isMinimized ? 'Llamada' : activeCall.title}
              </span>
              {isMinimized && (
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse shrink-0" title="En vivo" />
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0 ml-2">
              <button
                onClick={() => setIsMinimized((m) => !m)}
                className={`flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors ${
                  isMinimized ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                }`}
                title={isMinimized ? 'Expandir a pantalla completa' : 'Minimizar (ver pestañas)'}
                data-testid={isMinimized ? 'chat-expand-call-btn' : 'chat-minimize-call-btn'}
              >
                <i className={`fas ${isMinimized ? 'fa-expand' : 'fa-compress'}`} />
                {!isMinimized && <span className="hidden sm:inline">Minimizar</span>}
              </button>
              <button
                onClick={finalizar}
                className={`flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors ${
                  isMinimized ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                }`}
                data-testid="chat-end-call-btn"
              >
                <X className={isMinimized ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                {!isMinimized && (
                  <>
                    <span className="hidden sm:inline">Finalizar</span>
                    <span className="sm:hidden">Salir</span>
                  </>
                )}
              </button>
            </div>
          </div>
          <iframe
            src={activeCall.url}
            className="flex-1 w-full border-0"
            style={{ minHeight: 0 }}
            allow="camera *; microphone *; fullscreen; display-capture; autoplay; clipboard-write; picture-in-picture"
            allowFullScreen
            data-testid="chat-active-call-iframe"
            title={activeCall.title}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default ChatIncomingCall;
